import { useEffect, useMemo, useRef, useState } from "react";
import { openRowOnClick } from "../lib/rowOpen";
import { COACH_SEES_ENGINE } from "../lib/coachEngine";
import { myId } from "../lib/me";
import { supabase } from "../lib/supabase";
import { href, routeQuery } from "../lib/route";
import { Workspace } from "../components/Shell";
import { LibraryTabs, useLibraryCounts } from "../components/LibraryTabs";
import { AssignFromLibraryWindow } from "../components/AssignFromLibraryWindow";
import { calendarToday } from "../lib/days";
import { useQuerySort } from "../lib/useQuerySort";
import { displayName } from "../lib/roster";
import { duplicateWorkout } from "../lib/duplicateWorkout";
import { duplicateDeps, existingTemplateNames, readWorkoutUsage, removeDeps } from "../lib/workoutActionDeps";
import { removeWorkout, removedSentence, type WorkoutUsage } from "../lib/archiveWorkout";
import { RemoveWorkoutWindow } from "../components/RemoveWorkoutConfirm";
import { matchesWorkoutQuery } from "../lib/builder";
import { sortWorkouts, workoutSortFromQuery, workoutSortQuery, type WorkoutSortKey } from "../lib/workoutSort";
import {
  WORKOUT_CHIPS, chipCounts, inChip, listCaption, ruleGapCaption, workoutShelfRows,
  type WorkoutChip, type WorkoutShelfRow,
} from "../lib/workoutShelf";
import { hasRule } from "../lib/ruleSentence";
import { ShelfMore } from "../components/ShelfMore";
import { ActionStrip } from "../components/ActionStrip";
import { splitRow } from "../lib/actionStrip";

/* THE LIBRARY — WORKOUTS (W72 step 5, Proposal 5 with his no-chips-in-rows word).
 *
 * `Library` as the title, the four parts as ONE underline row, ONE primary `New workout`. The search, the chips (All ·
 * Assigned this week · Never assigned · Tasks, each with its count) and the sort; then the rows, 64 px: the name, the
 * ONE caption (`listCaption`: `6 exercises · Warm-up, Main work, Finisher · 1 superset` — W88, his yes 2026-09-28, which
 * folded the Exercises and Sections columns into it) and "N rows without a rule" as ACCENT TEXT — never a pill; on hover
 * `Assign` and ONE `⋯` holding Preview (the W17d overview window, unchanged) · Edit · Copy · Delete (job 3 step 5 page 4:
 * the row's click and its name already open the builder). `Next on` LEFT with W109 (Ioannis, 2026-09-30: "too much of
 * info that we don't need").
 *
 * 🔴 SORTED BY LAST USED — the address's sort survives (W23(b): `#/workouts?sort=name` is a real link), and with no
 * sort in it the list opens on last used, most recent first.
 *
 * ⚠️ A TEMPLATE IS `client_id is null`, and an ARCHIVED one is not in the library (V7-2's copies, his Delete =
 * archive) — the two predicates this list has always carried.
 */

/** The row's task's own action — drawn on hover; every other action is in the row's ONE `⋯` (job 3 step 5 page 4). */
const ON_ROW: readonly string[] = ["assign"];

const SORTS: readonly { key: WorkoutSortKey; label: string }[] = [
  { key: "lastUsed", label: "Last used" },
  { key: "name", label: "Name" },
  { key: "exercises", label: "Exercises" },
];

export function Workouts({ reloadKey = 0, foreground = true }: {
  reloadKey?: number;
  foreground?: boolean;
}) {
  const [rows, setRows] = useState<WorkoutShelfRow[] | null>(null);
  const [rules, setRules] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [clients, setClients] = useState<readonly { id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState<WorkoutChip>("all");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<WorkoutShelfRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [removing, setRemoving] = useState<
    | { kind: "idle" }
    | { kind: "measuring"; row: { id: string; name: string } }
    | { kind: "confirm"; row: { id: string; name: string }; usage: WorkoutUsage }
    | { kind: "working"; row: { id: string; name: string }; usage: WorkoutUsage }
    | { kind: "failed"; row: { id: string; name: string }; message: string; usage: WorkoutUsage | null }
  >({ kind: "idle" });
  const [localReload, setLocalReload] = useState(0);
  const [sort, setSortState] = useQuerySort(workoutSortFromQuery, routeQuery);
  const counts = useLibraryCounts(reloadKey + localReload);
  const heading = useRef<HTMLHeadingElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const today = useMemo(() => calendarToday(), []);

  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  function setSort(next: { key: WorkoutSortKey; dir: "asc" | "desc" }) {
    setSortState(next);
    window.history.replaceState(null, "", `${href({ name: "workouts" })}${workoutSortQuery(next)}`);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await myId();
      const { data, error: err } = await supabase
        .from("coach_workouts")
        .select("id, name, description, kind, coach_workout_sections(id, name, position, format, coach_workout_exercises(id, exercise_id, position, superset_with_next))")
        .eq("coach_id", me)
        .is("client_id", null)
        .is("archived_at", null)
        .order("name");
      if (err) { if (!cancelled) setError(err.message); return; }
      const { data: assigned, error: aErr } = await supabase
        .from("coach_assignments").select("workout_id, root_workout_id, client_id, scheduled_date");
      if (aErr) { if (!cancelled) setError(aErr.message); return; }
      /* The library's rules — a failure here is not this screen's failure: an EMPTY map means UNKNOWN at every
         reader, so no row claims a count before the library has answered. */
      const { data: lib } = await supabase.from("coach_exercises").select("id, name, progression_config").eq("coach_id", me);
      /* Who each assigned day belongs to — the coach's own relationships (names from the profile, else the invite). */
      const { data: links } = await supabase.from("coach_clients")
        .select("client_id, status, invited_first_name, invited_last_name, invited_email").eq("coach_id", me);
      const ids = (links ?? []).flatMap((l) => (l.client_id === null ? [] : [l.client_id]));
      const { data: people } = ids.length === 0 ? { data: [] }
        : await supabase.from("profiles").select("id, first_name, last_name").in("id", ids);
      if (cancelled) return;
      const nameById = new Map<string, string>();
      for (const l of links ?? []) {
        if (l.client_id === null) continue;
        const p = (people ?? []).find((x) => x.id === l.client_id);
        nameById.set(l.client_id, displayName(
          { firstName: p?.first_name ?? null, lastName: p?.last_name ?? null },
          { firstName: l.invited_first_name, lastName: l.invited_last_name, email: l.invited_email }));
      }
      setRows(workoutShelfRows(data ?? [], assigned ?? []));
      setRules(new Map((lib ?? []).map((e) => [e.id, hasRule(e.progression_config)])));
      setClients((links ?? []).filter((l) => l.status === "active" && l.client_id !== null)
        .map((l) => ({ id: l.client_id!, name: nameById.get(l.client_id!) ?? "Client" })));
    })();
    return () => { cancelled = true; };
  }, [reloadKey, localReload]);

  useEffect(() => {
    if (menuFor === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuFor(null); };
    const onDown = (e: MouseEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(e.target as Node)) setMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [menuFor]);

  const chipTotals = useMemo(() => chipCounts(rows ?? [], today), [rows, today]);
  /* FILTER (chip, then the name search), THEN SORT — the pure functions, none of them here. */
  const shown = useMemo(() => sortWorkouts(
    (rows ?? []).filter((w) => inChip(w, chip, today) && matchesWorkoutQuery(w.name, query)), sort.key, sort.dir),
  [rows, chip, today, query, sort.key, sort.dir]);

  /* 🔴 W108 (Ioannis, 2026-09-30: "we need to have a delete button too, needs to be more obvious cause he couldn't find it.
     maybe when hovering have all these buttons shown?") — A ROW'S ACTIONS ARE ONE LIST: the words on hover and the `⋯`
     where there is no hover both map it, so the two can never offer different things or do them differently. */
  type RowAction = {
    key: string; label: string; href?: string; onClick?: () => void; disabled?: boolean; destructive?: boolean;
  };
  function rowActions(w: WorkoutShelfRow): RowAction[] {
    return [
      { key: "preview", label: "Preview", href: href({ name: "workoutOverview", workoutId: w.id }) },
      { key: "assign", label: "Assign", disabled: clients.length === 0, onClick: () => setAssigning(w) },
      { key: "edit", label: "Edit", href: href({ name: "workout", workoutId: w.id }) },
      { key: "copy", label: "Copy", onClick: () => { void copy(w); } },
      { key: "delete", label: "Delete", destructive: true, onClick: () => { void onDeletePressed(w); } },
    ];
  }

  /* W108 — DELETE IS THE EXISTING ARCHIVE PATH ("his Delete = archive", `archiveWorkout.ts`), the builder's and the overview
     window's two-press flow in the builder's frame (`RemoveWorkoutWindow`): press 1 reads what points at the workout NOW
     (`readWorkoutUsage`) and the window states the consequence in the module's own words; press 2 is `removeWorkout` over
     the ONE `removeDeps`, which re-reads and only ever downgrades to an archive. Then the row leaves and the module's
     sentence says what happened. */
  async function onDeletePressed(w: WorkoutShelfRow) {
    setSaid(null);
    setRemoving({ kind: "measuring", row: { id: w.id, name: w.name } });
    const read = await readWorkoutUsage(w.id);
    if (read.error !== null || read.usage === null) {
      setRemoving({ kind: "failed", row: { id: w.id, name: w.name }, usage: null,
        message: `Could not check what uses this workout, so nothing was changed: ${read.error ?? "the check returned nothing."}` });
      return;
    }
    setRemoving({ kind: "confirm", row: { id: w.id, name: w.name }, usage: read.usage });
  }
  async function onRemoveConfirmed(row: { id: string; name: string }, usage: WorkoutUsage) {
    setRemoving({ kind: "working", row, usage });
    const outcome = await removeWorkout(removeDeps, row, new Date());
    if (outcome.kind === "failed") { setRemoving({ kind: "failed", row, message: outcome.message, usage }); return; }
    setRemoving({ kind: "idle" });
    setSaid(removedSentence(row.name, outcome));
    setLocalReload((n) => n + 1);
  }

  async function copy(row: WorkoutShelfRow) {
    setMenuFor(null);
    // The SAME `duplicateWorkout` the overview window and the builder's `⋯` call.
    const outcome = await duplicateWorkout(duplicateDeps, { id: row.id, name: row.name }, await existingTemplateNames());
    if (outcome.kind === "failed") { setError(outcome.message); return; }
    if (outcome.kind === "partial") setError(outcome.message);
    if (outcome.kind === "ok") window.location.hash = href({ name: "workout", workoutId: outcome.id });
    setLocalReload((n) => n + 1);
  }

  return (
    <Workspace>
      <main id="main" className="workouts-page ours-page library-page">
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" tabIndex={-1} ref={heading}>Library</h1>
          </div>
          <a className="ht-button ht-button-primary" href={href({ name: "workoutNew" })}>New workout</a>
        </header>
        <LibraryTabs current="workouts" counts={counts} />

        {error !== null && <p className="ht-callout ours-error" role="alert">{error}</p>}
        {said !== null && <p className="ht-callout ours-quiet" role="status">{said}</p>}
        {rows === null && error === null && <p className="ht-callout ours-quiet" aria-live="polite">Loading…</p>}
        {rows !== null && rows.length === 0 && (
          <p className="ht-callout ours-quiet">No workouts yet. A workout is a session you can assign to any client on any day.</p>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="shelf-controls">
            <label className="ours-search">
              <span className="ht-visually-hidden">Search workouts</span>
              <input className="ht-input" type="search" value={query} placeholder="Search workouts"
                     onChange={(e) => setQuery(e.target.value)} />
            </label>
            <div className="ours-chips" role="group" aria-label="Show">
              {WORKOUT_CHIPS.map((c) => (
                <button key={c.chip} type="button" className="ours-chip" aria-pressed={chip === c.chip}
                        onClick={() => setChip(c.chip)}>
                  {c.label} <span className="ours-chip-count">{chipTotals[c.chip]}</span>
                </button>
              ))}
            </div>
            <label className="ours-sort">
              Sort
              <select value={sort.key}
                      onChange={(e) => {
                        const key = e.target.value as WorkoutSortKey;
                        setSort({ key, dir: key === "name" ? "asc" : "desc" });
                      }}>
                {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>
          </div>
        )}

        {rows !== null && rows.length > 0 && shown.length === 0 && (
          <p className="ht-callout ours-quiet">
            {query.trim() !== "" ? `No workout here matches “${query.trim()}”.` : "Nothing under this chip."}
          </p>
        )}

        {shown.length > 0 && (
          <table className="ours-rows workout-rows">
            <caption className="ht-visually-hidden">Your workouts</caption>
            <thead>
              <tr>
                <th scope="col" className="ours-col-label">Workout</th>
                {/* 🔴 W88 (his yes, 2026-09-28): the EXERCISES and SECTIONS number columns folded into the row's caption.
                    W109 (Ioannis, 2026-09-30: "the next on is too much of info that we don't need"): the `Next on`
                    column LEFT, and its width is the name's and the caption's. The chips still say what is assigned. */}
                <th scope="col"><span className="ht-visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((w) => {
                const to = href({ name: "workout", workoutId: w.id });
                const caption = listCaption(w);
                const actions = splitRow(rowActions(w), ON_ROW);
                /* The rule gap is engine data, withheld from coaches for now (`COACH_SEES_ENGINE`, his 2026-09-28 word). */
                const gap = COACH_SEES_ENGINE ? ruleGapCaption(w.exerciseIds, rules) : null;
                return (
                  <tr key={w.id} className="ours-row workout-row row-opens" onClick={openRowOnClick(to)}>
                    <th scope="row" className="workout-name-cell">
                      <span className="ours-names">
                        <a className="ours-name" href={to}>{w.name}</a>
                        <span className="ours-caption workout-caption">
                          {caption}
                          {gap !== null && <> · <span className="workout-rule-gap">{gap}</span></>}
                        </span>
                      </span>
                    </th>
                    {/* 🔴 W108 — ONE list (`rowActions`). JOB 3 STEP 5 PAGE 4 (Fable's brief, 2026-10-09): on hover and
                        keyboard focus the row draws its task's own action, `Assign` (W119's `ActionStrip`), and ONE `⋯`
                        holding the rest in the list's order — Preview · Edit · Copy · Delete; the row's click and its
                        name are Edit's door already. The same two at every pointer (direction.css). */}
                    <td className="ours-actions shelf-actions">
                      <ActionStrip items={actions.strip} label={`Actions for ${w.name}`} />
                      <ShelfMore open={menuFor === w.id} label={w.name}
                                 wrapRef={menuFor === w.id ? menuRef : undefined}
                                 onToggle={() => setMenuFor((m) => (m === w.id ? null : w.id))}>
                        {actions.more.map((a) => (a.href !== undefined
                          ? <a key={a.key} href={a.href}>{a.label}</a>
                          : <button key={a.key} type="button" disabled={a.disabled}
                                    className={a.destructive ? "is-destructive" : undefined}
                                    onClick={() => { setMenuFor(null); a.onClick?.(); }}>{a.label}</button>))}
                      </ShelfMore>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* The foot says only what the tab does not: `N of M` under a chip or a search. Unfiltered it said the tab's own
            count again (`workoutCount` counts these same rows) — cut in job 3 step 5 page 4. */}
        {rows !== null && rows.length > 0 && shown.length !== rows.length && (
          <p className="ours-foot">{`${String(shown.length)} of ${String(rows.length)} workouts`}</p>
        )}

        <RemoveWorkoutWindow open={removing.kind !== "idle"} name={removing.kind === "idle" ? "" : removing.row.name}
                             usage={removing.kind === "confirm" || removing.kind === "working" || removing.kind === "failed"
                               ? removing.usage : null}
                             working={removing.kind === "working"}
                             error={removing.kind === "failed" ? removing.message : null}
                             onCancel={() => setRemoving({ kind: "idle" })}
                             onConfirm={() => {
                               if ((removing.kind === "confirm" || removing.kind === "failed") && removing.usage !== null) {
                                 void onRemoveConfirmed(removing.row, removing.usage);
                               }
                             }} />
        {assigning !== null && (
          <AssignFromLibraryWindow workout={assigning} clients={clients} today={today}
                             onClose={() => setAssigning(null)}
                             onAssigned={(s) => { setSaid(s); setAssigning(null); setLocalReload((n) => n + 1); }} />
        )}
      </main>
    </Workspace>
  );
}
